/**
 * Case Intelligence Chat API Service
 */

import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface ToolCall {
  tool_name: string;
  parameters: Record<string, any>;
  result: Record<string, any>;
}

export interface Citation {
  source_type: string;
  source_name: string;
  details: string;
  excerpt?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tool_calls?: ToolCall[];
  citations?: Citation[];
}

export interface ChatResponse {
  response: string;
  tool_calls: ToolCall[];
  citations: Citation[];
  suggested_followups: string[];
  conversation_id: string;
  tokens_used?: number;
}

export interface ToolInfo {
  name: string;
  description: string;
  parameters: Record<string, any>;
}

class IntelligenceApi {
  private async getHeaders(): Promise<HeadersInit> {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated: unable to retrieve auth token');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async sendMessage(
    reportId: string,
    message: string,
    conversationId?: string,
    voiceMode: boolean = false
  ): Promise<ChatResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        report_id: reportId,
        message,
        conversation_id: conversationId,
        voice_mode: voiceMode
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Chat failed: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversationHistory(conversationId: string): Promise<ChatMessage[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/intelligence/conversations/${conversationId}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(`Failed to fetch history: ${response.statusText}`);
    }

    const data = await response.json();
    return data.messages || [];
  }

  async getAvailableTools(): Promise<ToolInfo[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/tools`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch tools: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tools || [];
  }

  async healthCheck(): Promise<{ status: string; llm_configured: boolean }> {
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/health`);
    if (!response.ok) {
      throw new Error('Intelligence service unavailable');
    }
    return response.json();
  }
}

export const intelligenceApi = new IntelligenceApi();
export default intelligenceApi;
